<script lang="ts">
import type { CSSProperties } from 'vue'
export type TextVariant = 'subtitle' | 'normal' | 'large' | 'small' | 'caption'

type Unit = 'px' | 'pt'
export type Size = `${number}${Unit}`

export interface ITextStyle extends CSSProperties {
  fontFamily?: 'Roboto' | 'Inter'
  fontStyle?: 'normal'
  fontSize: Size
  lineHeight: Size
  letterSpacing?: `${number}em`
}

export default {
  // eslint-disable-next-line vue/no-reserved-component-names
  name: 'Text',
}
</script>

<script setup lang="ts">
interface IProps {
  bold?: boolean
  color?: string
  variant?: TextVariant
  maxLines?: number
  title?: string
  fontWeight?: number
  overflow?: boolean
  isGradientColor?: boolean
  linkStyling?: boolean
}
withDefaults(
  defineProps<IProps>(),
  {
    variant: 'normal',
    overflow: false,
  },
)

const styles: Record<TextVariant, ITextStyle> = {
  subtitle: {
    // TODO: add "Archivo Narrow" font family
    fontFamily: '\'Inter\'',
    fontDisplay: 'swap',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.04em',
  },
  normal: {
    fontFamily: '\'Inter\'',
    fontDisplay: 'swap',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
  },
  large: {
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '18px',
  },
  small: {
    fontFamily: '\'Inter\'',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '16px',
  },
  caption: {
    fontFamily: '\'Inter\'',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '10px',
    lineHeight: '14px',
  },
}
</script>

<template>
  <p
    class="text default:text-black default:leading-5"
    :class="[isGradientColor && 'text-gradient', linkStyling && 'style-link']"
    :style="{
      ...styles[variant],
      'color': color || undefined,
      'fontWeight': bold ? 700 : (fontWeight || styles[variant].fontWeight),
      'overflow': maxLines || overflow ? 'hidden' : 'visible',
      'textOverflow': maxLines || overflow ? 'ellipsis' : 'visible',
      'display': overflow ? 'block' : '-webkit-box',
      '-webkit-line-clamp': maxLines,
      '-webkit-box-orient': 'vertical',
    }"
    :title="title"
  >
    <slot />
  </p>
</template>

<style lang="scss">
.text {
  font-family: Inter, sans-serif;
  word-wrap: break-word;

  & h1 {
    font-size: 2em;
    line-height: 2.5rem;
  }

  & h2 {
    font-size: 1.5em;
    line-height: 2rem;
  }

  & h3 {
    font-size: 14px;
    line-height: 21px;
  }

  & ol {
    padding-left: 2rem;
    list-style: decimal;
  }

  & ul {
    padding-left: 2rem;
    list-style: disc;
  }

  & blockquote {
    @apply border-l-4 border-soft-gray pl-4 my-1;
  }

  &.style-link a {
    color: #06c;
    @apply underline;
  }
}

.text-gradient {
  background: var(--gradient-1, linear-gradient(191deg, #35DBFF 0%, #B09EF8 42.71%, #FD93E7 96.95%));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>
